import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ClientPortalState } from "../../redux";

export * from "./bookingSlice.types";
import { AppointmentBookingClientModel, ProviderModel } from "types/generated/models";
import {
    saveBookingDataToLocalStorage,
    loadBookingDataFromLocalStorage,
    clearBookingDataFromLocalStorage,
    clearAllBookingDataFromLocalStorage,
    LocalStorageBookingData,
} from "./AppointmentBooking.helpers";
import { BookingType, IAvailabilityData, IGroupData, IProviderData } from "types/appointmentBooking";
import {
    StepId,
    STEP_LOCATION,
    STEP_SERVICE,
    STEP_PROVIDER,
    STEP_AVAILABILITY,
    STEP_REGISTER,
    STEP_CONFIRMATION,
    IBookingState,
    IServiceState,
    IRegistrationState,
    INavigationState,
    ISessionState,
    BookingStatus,
    ILocationState,
    IProviderState,
    IGroupState,
    IConfirmationState,
    IAvailabilityState,
    ILocationData,
    IConfirmationData,
    IServiceData,
    IPaymentData,
} from "./bookingSlice.types";

const ALL_VALID_STEPS: StepId[] = [
    STEP_LOCATION,
    STEP_SERVICE,
    STEP_PROVIDER,
    STEP_AVAILABILITY,
    STEP_REGISTER,
    STEP_CONFIRMATION,
];

const saveBookingToLocalStorage = (state: IBookingState) => {
    if (!state.session.clientPortalGuid) return;

    const dataToSave: LocalStorageBookingData = {
        location: state.location.data,
        service: state.service.data,
        provider: state.provider.data,
        group: state.group.data,
        availability: state.availability.data,
        confirmation: state.confirmation.data,
        currentStep: state.navigation.currentStep,
        timestamp: Date.now(),
    };

    saveBookingDataToLocalStorage(dataToSave, state.session.clientPortalGuid, state.session.bookingType);
};

const clearDependentStepsInState = (state: IBookingState, fromStep: StepId) => {
    const fromIndex = ALL_VALID_STEPS.indexOf(fromStep);

    for (const step of ALL_VALID_STEPS.slice(fromIndex + 1)) {
        switch (step) {
            case STEP_SERVICE:
                state.service.data = null;
                state.service.isCompleted = false;
                state.service.selectedCategory = undefined;
                break;
            case STEP_PROVIDER:
                state.provider.data = null;
                state.provider.isCompleted = false;
                break;
            case STEP_AVAILABILITY:
                state.availability.data = null;
                state.availability.isCompleted = false;
                state.group.data = null;
                state.group.isCompleted = false;
                break;
            case STEP_REGISTER:
                state.registration.isCompleted = false;
                break;
            case STEP_CONFIRMATION:
                state.confirmation.data = null;
                state.confirmation.isCompleted = false;
                break;
        }
    }
};

const initialStepState = {
    data: null,
    isCompleted: false,
} as const;

const initialServiceState: IServiceState = {
    data: null,
    isCompleted: false,
    selectedCategory: undefined,
};

const initialRegistrationState: IRegistrationState = {
    isCompleted: false,
};

const initialNavigationState: INavigationState = {
    currentStep: STEP_LOCATION,
};

const initialSessionState: ISessionState = {
    cancelModalOpen: false,
    clientPortalGuid: undefined,
    bookingStatus: BookingStatus.IN_PROGRESS,
    bookingType: BookingType.CLIENT,
    completedBookingData: null,
};

const initialBookingState: IBookingState = {
    location: { ...initialStepState } as ILocationState,
    service: { ...initialServiceState },
    provider: { ...initialStepState } as IProviderState,
    group: { ...initialStepState } as IGroupState,
    availability: { ...initialStepState } as IAvailabilityState,
    registration: { ...initialRegistrationState },
    confirmation: { ...initialStepState } as IConfirmationState,
    navigation: { ...initialNavigationState },
    session: { ...initialSessionState },
    availableProviders: [],
};

const resetStateToInitial = (state: IBookingState) => {
    state.location = { ...initialStepState } as ILocationState;
    state.service = { ...initialServiceState };
    state.provider = { ...initialStepState } as IProviderState;
    state.group = { ...initialStepState } as IGroupState;
    state.availability = { ...initialStepState } as IAvailabilityState;
    state.registration = { ...initialRegistrationState };
    state.confirmation = { ...initialStepState } as IConfirmationState;
    state.navigation = { ...initialNavigationState };
    state.session.cancelModalOpen = false;
    state.session.bookingStatus = BookingStatus.IN_PROGRESS;
    state.session.completedBookingData = null;
};

const setStepData = (
    state: IBookingState,
    stepName: keyof Pick<
        IBookingState,
        "location" | "service" | "provider" | "group" | "availability" | "confirmation"
    >,
    data: ILocationData | IServiceData | IProviderData | IGroupData | IAvailabilityData | IConfirmationData
) => {
    state[stepName].data = data;
    state[stepName].isCompleted = true;
};

const applySavedStepData = (state: IBookingState, savedData: LocalStorageBookingData, bookingType: BookingType) => {
    if (savedData.location) setStepData(state, "location", savedData.location);

    if (savedData.service && bookingType === BookingType.CLIENT) {
        setStepData(state, "service", savedData.service);
    }

    if (savedData.provider) setStepData(state, "provider", savedData.provider);

    if (savedData.group && bookingType === BookingType.CLASS) {
        setStepData(state, "group", savedData.group);
    }

    if (savedData.availability) setStepData(state, "availability", savedData.availability);
    if (savedData.confirmation) setStepData(state, "confirmation", savedData.confirmation);
};

const determineCurrentStep = (savedData: LocalStorageBookingData, bookingType: BookingType): StepId => {
    if (savedData.currentStep && ALL_VALID_STEPS.includes(savedData.currentStep)) {
        if (savedData.currentStep === STEP_SERVICE && bookingType === BookingType.CLASS) {
            return STEP_PROVIDER;
        }
        return savedData.currentStep;
    }

    return STEP_LOCATION;
};

const withNeedsDeposit = (service: IServiceData): IServiceData => ({
    ...service,
    needsDeposit: (service.portalDeposit ?? 0) > 0,
});

export const bookingSlice = createSlice({
    name: "booking",
    initialState: initialBookingState,
    reducers: {
        initializeBooking: (state, action: PayloadAction<{ clientPortalGuid: string; bookingType: BookingType }>) => {
            const { clientPortalGuid, bookingType } = action.payload;

            resetStateToInitial(state);

            state.session.clientPortalGuid = clientPortalGuid;
            state.session.bookingType = bookingType;

            const savedData = loadBookingDataFromLocalStorage(clientPortalGuid, bookingType);
            if (!savedData) {
                return;
            }

            applySavedStepData(state, savedData, bookingType);
            state.navigation.currentStep = determineCurrentStep(savedData, bookingType);
        },

        setLocation: (state, action: PayloadAction<ILocationData>) => {
            clearDependentStepsInState(state, STEP_LOCATION);
            state.location.data = action.payload;
            state.location.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setService: (state, action: PayloadAction<IServiceData>) => {
            clearDependentStepsInState(state, STEP_SERVICE);
            state.service.data = withNeedsDeposit(action.payload);
            state.service.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setServiceCategory: (state, action: PayloadAction<string>) => {
            state.service.selectedCategory = action.payload;
        },

        setServiceForClass: (state, action: PayloadAction<IServiceData>) => {
            state.service.data = withNeedsDeposit(action.payload);
            saveBookingToLocalStorage(state);
        },

        setProvider: (state, action: PayloadAction<IProviderData>) => {
            clearDependentStepsInState(state, STEP_PROVIDER);
            state.provider.data = action.payload;
            state.provider.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setAvailableProviders: (state, action: PayloadAction<ProviderModel[]>) => {
            state.availableProviders = action.payload;
        },

        setGroup: (state, action: PayloadAction<IGroupData>) => {
            state.group.data = action.payload;
            state.group.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setAvailability: (state, action: PayloadAction<IAvailabilityData>) => {
            state.availability.data = action.payload;
            state.availability.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setNotes: (state, action: PayloadAction<string>) => {
            state.confirmation.data = { ...state.confirmation.data, notes: action.payload };
            state.confirmation.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        setClient: (state, action: PayloadAction<AppointmentBookingClientModel>) => {
            state.confirmation.data = { ...state.confirmation.data, client: action.payload };
            state.confirmation.isCompleted = true;
            saveBookingToLocalStorage(state);
        },

        goToStep: (state, action: PayloadAction<StepId>) => {
            const step = action.payload;

            if (ALL_VALID_STEPS.includes(step)) {
                state.navigation.currentStep = step;
                saveBookingToLocalStorage(state);
            }
        },

        setCancelModalOpen: (state, action: PayloadAction<boolean>) => {
            state.session.cancelModalOpen = action.payload;
        },

        setClientPortalGuid: (state, action: PayloadAction<string>) => {
            state.session.clientPortalGuid = action.payload;
        },

        completeBookingAndClear: (state, action: PayloadAction<IPaymentData | null>) => {
            state.session.completedBookingData = {
                location: state.location.data,
                service: state.service.data,
                provider: state.provider.data,
                group: state.group.data,
                availability: state.availability.data,
                confirmation: state.confirmation.data,
                payment: action.payload || null,
            };
            state.session.bookingStatus = BookingStatus.BOOKED;
            state.navigation.currentStep = STEP_CONFIRMATION;

            if (state.session.clientPortalGuid) {
                clearBookingDataFromLocalStorage(state.session.clientPortalGuid, state.session.bookingType);
            }
        },

        clearBookingState: (state, action?: PayloadAction<BookingType | "all">) => {
            if (!state.session.clientPortalGuid) return;

            const clearType = action?.payload;
            const currentBookingType = state.session.bookingType;

            if (clearType === "all") {
                clearAllBookingDataFromLocalStorage(state.session.clientPortalGuid);
            } else {
                clearBookingDataFromLocalStorage(state.session.clientPortalGuid, clearType ?? currentBookingType);
            }

            const shouldReset = !clearType || clearType === "all" || clearType === currentBookingType;
            if (shouldReset) {
                resetStateToInitial(state);
            }
        },
    },
});

export const {
    initializeBooking,
    setLocation,
    setService,
    setServiceCategory,
    setServiceForClass,
    setProvider,
    setAvailableProviders,
    setGroup,
    setAvailability,
    setNotes,
    setClient,
    goToStep,
    setCancelModalOpen,
    setClientPortalGuid,
    completeBookingAndClear,
    clearBookingState,
} = bookingSlice.actions;

export const selectBooking = (state: ClientPortalState) => state.booking;
export const selectBookingType = (state: ClientPortalState) => state.booking.session.bookingType;

export default bookingSlice.reducer;
